<template>
  <v-dialog
    :value="value"
    width="900px"
    persistent
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title>
        API Partner Setup
        <v-spacer />
        <v-btn icon @click="cancel">
          <v-icon>mdi-close-circle</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert dense text type="info" class="body-2">
          SD will generate OAuth2 credentials (Client ID + Client Secret) for
          this facility. The Client Secret downloads immediately and is shown
          only once — regenerate it if lost.
        </v-alert>

        <v-row dense class="mt-2">
          <v-col md="6">
            <v-text-field
              v-model="name"
              label="Name"
              hide-details="auto"
              :rules="[requiredRule]"
            />
          </v-col>
        </v-row>

        <v-row dense class="mt-4">
          <v-col md="6">
            <v-text-field
              :value="partnerName"
              label="Partner / Integration Name"
              disabled
              hide-details="auto"
            />
          </v-col>
        </v-row>

        <v-row dense class="mt-2">
          <v-col md="3">
            <v-text-field
              :value="tokenExpiryHours"
              label="Token Expiry (hours)"
              disabled
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" class="pt-0">
            <div class="caption grey--text">
              Access token lifetime. Partner re-authenticates via POST
              /api/v1/auth/token on 401. OAuth2 credentials (Client ID + Secret)
              do not expire.
            </div>
          </v-col>
        </v-row>

        <v-row dense class="mt-4">
          <v-col md="3">
            <v-text-field
              :value="callLimit"
              label="Daily API Call Limit"
              disabled
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" class="pt-0">
            <div class="caption grey--text">
              Maximum number of API calls allowed per day for this facility.
              Default is 1000.
            </div>
          </v-col>
        </v-row>

        <v-alert
          v-if="!hasConfig"
          dense
          text
          type="warning"
          class="body-2 mt-3"
        >
          No existing API configuration for this facility. Ask your
          administrator to set it up first.
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text color="blue darken-1" @click="cancel">Cancel</v-btn>
        <v-btn
          color="primary"
          :disabled="!canSubmit || saving"
          :loading="saving"
          @click="submit"
        >
          <v-icon left>mdi-download</v-icon>
          Generate New Credential
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useGenerateCredential, resolveSdApiClient } from "./useApiIntegration";

export default {
  name: "ApiSettingsDialog",
  props: {
    value: { type: Boolean, default: false },
    organizationId: { type: String, default: null },
    current: { type: Object, default: null },
  },
  setup() {
    const { generateCredential } = useGenerateCredential();
    return { generateCredential };
  },
  data() {
    return {
      name: "",
      saving: false,
    };
  },
  computed: {
    hasConfig() {
      return !!this.current && !!this.current.partnerId;
    },
    partnerName() {
      return (this.current && this.current.partnerName) || "--";
    },
    tokenExpiryHours() {
      return this.current
        ? Math.round((this.current.tokenExpiry || 0) / 3600)
        : "--";
    },
    callLimit() {
      return this.current && this.current.callLimit != null
        ? this.current.callLimit
        : "--";
    },
    canSubmit() {
      return this.hasConfig && !!this.name && this.name.trim().length > 0;
    },
  },
  watch: {
    value(open) {
      if (open) this.name = "";
    },
  },
  methods: {
    requiredRule(value) {
      return (!!value && value.trim().length > 0) || "Required";
    },
    async submit() {
      this.saving = true;
      try {
        const credential = await this.generateCredential(
          resolveSdApiClient(this.$apollo),
          {
            organizationId: this.organizationId,
            partnerId: this.current.partnerId,
            name: this.name,
            tokenExpiry: this.current.tokenExpiry,
            callLimit: this.current.callLimit,
          }
        );
        this.$emit("generated", credential);
        this.$emit("input", false);
      } catch {
        // Error surfaced via useAlerts inside the composable.
      } finally {
        this.saving = false;
      }
    },
    cancel() {
      this.$emit("input", false);
    },
  },
};
</script>
